import { toast } from "$global-providers/toast-provider";
import { gql } from "@apollo/client";
import { PayerType } from "elephant-web/gql";
import { useIntl } from "react-intl";
import type { Payer } from "../InsurerAccordion";
import { useAddPayersMutation } from "./__generated__/useUpdateInsurers.gql";

gql`
  mutation addPayers($input: AddPayersInput!) {
    addPayers(input: $input) {
      _id
      payers {
        type
        name
        balance {
          total
          paid
          due
        }
        insuranceDetails {
          name
          insuranceNumber
          nationalIdNumber
          dependent
        }
      }
    }
  }
`;

export const useTransformPayers = (insurers: Payer[]) => {
  const formattedInsurers = insurers.map((i) => ({
    name: i?.name || i?.type,
    type: PayerType.Insurer,
    balance: {
      paid: 0,
      due: i?.balance?.total ?? 0,
      total: i?.balance?.total ?? 0,
    },
    insuranceDetails:
      (i?.insuranceDetails && {
        name:
          i.insuranceDetails.schemeName ||
          i.insuranceDetails.name ||
          i?.name ||
          i?.type,
        nationalIdNumber: i.insuranceDetails.nationalIdNumber,
        insuranceNumber: i.insuranceDetails.insuranceNumber,
        dependent: i.insuranceDetails.dependent,
      }) ||
      undefined,
  }));
  return formattedInsurers;
};

export default function useUpdateInsurers(payers: Payer[], invoiceId: string) {
  const { formatMessage } = useIntl();
  const [updateInsurers] = useAddPayersMutation({
    variables: {
      input: {
        _id: invoiceId,
        payers: useTransformPayers(payers),
      },
    },
    onError() {
      toast.error(
        formatMessage({
          id: "invoicing.updateInsurers.error.msg",
          defaultMessage: "Failed to update insurers",
        }),
      );
    },
    onCompleted() {
      toast.success(
        formatMessage({
          id: "invoicing.updateInsurers.success.msg",
          defaultMessage: "Insurers updated successfully",
        }),
      );
    },
  });

  return { updateInsurers };
}
